import React from 'react';
import { BarChart, Trash2, Layers, Clock } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сохраненных расчетов НДС
 */
const SavedCalculationsSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-green-500" />
          Сохраненные расчеты
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты НДС для быстрого доступа и сравнения.
        </p>
      </div>
    );
  }

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Получение типа расчета в виде текста
  const getCalculationTypeText = (type) => {
    return type === 'add' ? 'Начисление' : 'Выделение';
  };

  // Получение ставки НДС в виде текста
  const getVatRateText = (rate) => {
    if (rate === 0) return '0%';
    return `${rate}%`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-green-500" />
          Сохраненные расчеты
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'расчет' : 
                                     savedCalculations.length < 5 ? 'расчета' : 'расчетов'}
        </span>
      </div>

      <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить расчет"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-sm text-gray-600 dark:text-gray-400 mb-2">
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3 flex-shrink-0" />
                <span className="text-xs">{formatDate(calc.date)}</span>
              </div>
              <div className="text-right">
                {getCalculationTypeText(calc.formData.calculationType)}, {
                  calc.formData.vatRate === 'none' ? 'Без НДС' : 
                  calc.formData.vatRate === 'custom' ? `НДС ${calc.results.vatRate}%` : 
                  `НДС ${calc.formData.vatRate}%`
                }
              </div>
            </div>
            
            <div className="text-sm">
              <div className="flex justify-between mb-1">
                <span>Сумма:</span>
                <span className="font-medium">
                  {calc.formData.calculationType === 'add' ? 
                    formatCurrency(calc.results.amountWithoutVAT, calc.results.precision) :
                    formatCurrency(calc.results.amountWithVAT, calc.results.precision)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>НДС:</span>
                <span>
                  {formatCurrency(calc.results.vatAmount, calc.results.precision)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>{calc.formData.calculationType === 'add' ? 'Итого с НДС:' : 'Без НДС:'}</span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {calc.formData.calculationType === 'add' ? 
                    formatCurrency(calc.results.amountWithVAT, calc.results.precision) :
                    formatCurrency(calc.results.amountWithoutVAT, calc.results.precision)}
                </span>
              </div>
            </div>
            
            {calc.formData.description && (
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500 border-t border-gray-200 dark:border-gray-600 pt-2">
                {calc.formData.description}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavedCalculationsSidebar;